import { ITimeSpan } from "@interfaces/ITimeSpan";
import { ColourService } from "@services/colour.service";
import { MapService } from "@services/map.service";
import * as angular from "angular";
import * as moment from "moment";
import { IPlannedRouteVisit } from "../../modules/tms/_models/IPlannedRouteVisit";
import { ITMSJob } from "./interfaces/ITMSJob";

export enum RouteEditingDisabledReason {
    Editable,
    Start,
    Status
}

export enum PlannedRouteStatus {
    Planned,
    Started,
    Completed,
    CompletedWithMissing,
    Cancelled,
    Expired,
    Incomplete
}

export enum RouteVisitStatus {
    Planned,
    Next,
    Arrived,
    Departed,
    Missed
}

export interface IOptimiserRequest {

    maxDistanceKmPerVehicle: number;
    maxDistanceKmSlack: number;

    numVehicles: number;
    maxWorkingTimeHours: number;
    maxDrivingTimeHours: number;
    onSiteTimeMins: number;
    breakRequired: number;

    depotIndex: number;
    locations: IUnplannedLocation[];
}

export interface IOptimiserResponse {
    OptimisedVehicleRoutes: IOptimisedVehicleRoute[];

    DroppedLocations: IUnplannedLocation[];

    stats?: IOptimiserResponseStats;

    originalMapLink: string;
    optimisedMapLink: string;
}

export interface IOptimiserResponseStats {
    originalDistance: number;
    optimisedDistance: number;

    distancePercentage: number;
    distanceSavingPercentage: number;

    originalDurationMinutes: number;
    optimisedDurationMinutes: number;

    durationPercentage: number;
    durationSavingPercentage: number;
}

export interface IOptimisedVehicleBreak {
    DurationSeconds: number;
    Name: string;
    StartSecond: number;
}

export interface IOptimisedRouteTimelineEntry {
    title: string;
    arrivalTime: moment.Moment;
    departureTime: moment.Moment;

    travelDistance: number;
    travelTime: number;

    activityDuration: number;

    cumulativeDistance: number;
    cumulativeDuration: number;

    icon?: string;
    isBreak?: boolean;
}

export interface IOptimisedVehicleRoute {

    VehicleId: number;
    OptimisedLocations: IOptimisedLocation[];
    Breaks: IOptimisedVehicleBreak[];

    timeline: IOptimisedRouteTimelineEntry[];

    visitCount: number;

    TotalDistance: number;
    TotalDistanceKm: number;

    TravelTime: ITimeSpan;
    WorkingTime: ITimeSpan;

    // ---

    colour: string;
}

export interface IOptimisedLocation/* extends IOptimiserLocation*/ {
    DistanceToStop: number;
    TravelTimeSeconds: number;

    OriginalLocation: IUnplannedLocation;
}

export interface IUnplannedVisit extends IUnplannedLocation {
    jobs: ITMSJob[];
}

export interface IUnplannedLocation {
    Id: number;
    Title: string;
    Latitude: number;
    Longitude: number;
    OnSiteTimeMins: number;
}
export interface IUnplannedJobLocation { }

export class OptimiserService {

    static $inject = ["$http", "$q", "colourService", "mapService"];

    constructor(
        private readonly $http: angular.IHttpService,
        private readonly $q: angular.IQService,
        private readonly colourService: ColourService,
        private readonly mapService: MapService) {
    }

    createUnplannedLocationsFromJobs(jobs: ITMSJob[], onSiteTimeMins: number = 5): IUnplannedLocation[] {
        const locations: IUnplannedLocation[] = [];

        jobs.forEach(job => {
            const location: IUnplannedLocation = {
                Id: job.Id,
                Latitude: job.geofence.Latitude,
                Longitude: job.geofence.Longitude,
                Title: job.geofence.Addr1,
                OnSiteTimeMins: onSiteTimeMins//,30 // owens test
            };

            if (job.geofence.Addr2) {
                if (location.Title.trim() != "") {
                    location.Title += ", ";
                }

                location.Title += job.geofence.Addr2
            }

            locations.push(location);
        });

        return locations;
    }

    createUnplannedLocationsFromVisits(visits: IPlannedRouteVisit[]): IUnplannedLocation[] {

        const locations: IUnplannedLocation[] = [];

        visits.forEach(v => {

            if (!v.RouteStopLocation) {
                return;
            }

            const location: IUnplannedLocation = {
                Id: v.Id,
                Latitude: v.RouteStopLocation.Latitude,
                Longitude: v.RouteStopLocation.Longitude,
                Title: v.Name,
                OnSiteTimeMins: v.onSiteTimeMins//,30 // owens test
            };

            locations.push(location);
        });

        return locations;
    }

    createDefaultOptimiserRequest(): IOptimiserRequest {
        var optimiserRequest: IOptimiserRequest = {
            locations: [],
            maxDistanceKmPerVehicle: 1000,
            maxDistanceKmSlack: 5,
            numVehicles: 50,
            maxWorkingTimeHours: 13,
            maxDrivingTimeHours: 9,
            depotIndex: 0,
            onSiteTimeMins: 5,
            breakRequired: 45
        };

        return optimiserRequest;
    }

    createGoogleMapsLink(locations: IUnplannedLocation[]): string {
        //https://www.google.com/maps/dir/?api=1&origin=nn40tz&waypoints=nn54ap&destination=nn47bf

        console.log("create maps link", locations.length, locations)

        var url = "https://www.google.com/maps/dir/?api=1";
        url += `&origin=${locations[0].Title}`;
        //url += `&destination=${locations[locations.length - 1].title}`;

        url += "&waypoints=";
        locations.forEach(loc => { url += `${loc.Title}|`; });

        // Return to depot
        url += `&destination=${locations[0].Title}`;

        return url;
    }

    createOptimiserRequestFromJobs(depot: IUnplannedLocation, jobs: ITMSJob[]): IOptimiserRequest {
        var optimiseRequest = this.createDefaultOptimiserRequest();

        optimiseRequest.locations = this.createUnplannedLocationsFromJobs(jobs);

        // Ensure depot is first index, and we filter out returns to depot keeping our search space small
        //var depot = optimiseRequest.locations.find(l => l.title.indexOf("NN17 5XZ") >= 0);
        //if (!depot) {
        //    depot = optimiseRequest.locations[0]
        //};

        //optimiseRequest.locations = optimiseRequest.locations.filter(l => l != depot && l.title.indexOf("NN17 5XZ") < 0);
        this.setDepot(depot, optimiseRequest);

        return optimiseRequest;
    }

    setDepot(depot: IUnplannedLocation, optimiserRequest: IOptimiserRequest) {
        if (depot) {
            optimiserRequest.locations.unshift(depot);
            optimiserRequest.depotIndex = 0;
        }
    }

    //createOptimiserRequestFromTMSVisits(stops: IUnplannedVisit[]): IOptimiserRequest { }

    calculateOptimiserStats(optimiseRequest: IOptimiserRequest, optimiserResponse: IOptimiserResponse) {
        console.log("// -----------------")
        console.log("// CalculateOptimiserStats")
        console.log("// -----------------")

        console.log();

        // Calculate (in current form) total distance
        var totalDistance = 0;
        var previousLocation = optimiseRequest.locations[0];

        optimiseRequest.locations.forEach((location, index) => {
            if (index == 0) {
                return;
            }

            totalDistance += this.mapService.getDistance(previousLocation.Latitude, previousLocation.Longitude, location.Latitude, location.Longitude);
            previousLocation = location;
        });

        const totalTime = totalDistance / 64;

        console.log("// Original distance:", `${totalDistance}Km`);
        console.log("// Original time (64kph / 40mph):", `${totalTime}hours`); // 40 /** 1.60934*/

        console.log();

        var newDistance = optimiserResponse.OptimisedVehicleRoutes.reduce((total, ovr) => total + ovr.TotalDistanceKm, 0);
        var newTime = optimiserResponse.OptimisedVehicleRoutes.reduce((total, ovr) => total + ovr.TravelTime.totalHours, 0);

        console.log("// New distance:", `${newDistance}Km`);
        console.log("// New time (64kph / 40mph):", `${newTime}hours`); // 40 /** 1.60934*/

        const distancePercentage = newDistance / totalDistance * 100;
        const distanceSavingPercentage = 100 - distancePercentage;

        const durationPercentage = newTime / totalTime * 100;
        const durationSavingPercentage = 100 - durationPercentage;

        optimiserResponse.stats = {
            originalDistance: totalDistance,
            optimisedDistance: newDistance,

            distancePercentage: distancePercentage,
            distanceSavingPercentage: distanceSavingPercentage,

            originalDurationMinutes: totalTime * 60,
            optimisedDurationMinutes: newTime * 60,

            durationPercentage: durationPercentage,
            durationSavingPercentage: durationSavingPercentage
        };
    }

    optimise(optimiseRequest: IOptimiserRequest): angular.IPromise<IOptimiserResponse> {
        return this
            .$http({
                url: "/api/optimiser",
                method: "POST",
                data: optimiseRequest
            })
            .then((response: angular.IHttpPromiseCallbackArg<IOptimiserResponse>) => {

                response.data.OptimisedVehicleRoutes.forEach((ovr, i) => {
                    ovr.colour = this.colourService.getVigetColor(i, response.data.OptimisedVehicleRoutes.length);
                    ovr.VehicleId = i + 1;
                    ovr.visitCount = ovr.OptimisedLocations.length - 2 - ovr.Breaks.length;

                    this.createRouteTimeline(ovr);
                });

                this.calculateOptimiserStats(optimiseRequest, response.data);

                response.data.originalMapLink = this.createGoogleMapsLink(optimiseRequest.locations);
                response.data.optimisedMapLink = this.createGoogleMapsLink(response.data.OptimisedVehicleRoutes[0].OptimisedLocations.map(l => l.OriginalLocation));

                //console.log("response.data.originalMapLink", response.data.originalMapLink);
                //console.log("response.data.optimisedMapLink", response.data.optimisedMapLink);

                return response.data;
            });
    }

    // ---

    private createRouteTimeline(ovr: IOptimisedVehicleRoute) {

        // Create timeline
        var shiftStartTime = moment().hour(8).minute(0).second(0);
        ovr.timeline = [];
        var previousTimelineEntry: IOptimisedRouteTimelineEntry = null;
        var takenBreak = false;

        ovr.OptimisedLocations.forEach(location => {
            if (!takenBreak && previousTimelineEntry && previousTimelineEntry.cumulativeDuration + location.TravelTimeSeconds > ovr.Breaks[0].StartSecond) {
                const breakEntry = this.createRouteTimelineEntry(
                    previousTimelineEntry,
                    ovr.Breaks[0].Name,
                    0,
                    0,
                    ovr.Breaks[0].DurationSeconds,
                    shiftStartTime);

                breakEntry.arrivalTime = shiftStartTime.clone().add(ovr.Breaks[0].StartSecond, "s");
                breakEntry.departureTime = breakEntry.arrivalTime.clone().add(ovr.Breaks[0].DurationSeconds, "s");
                breakEntry.isBreak = true;

                ovr.timeline.push(breakEntry);
                previousTimelineEntry = breakEntry;

                takenBreak = true;
            }

            const timelineEntry = this.createRouteTimelineEntry(
                previousTimelineEntry,
                location.OriginalLocation.Title,
                location.DistanceToStop,
                location.TravelTimeSeconds,
                location.OriginalLocation.OnSiteTimeMins * 60,
                shiftStartTime);

            ovr.timeline.push(timelineEntry);
            previousTimelineEntry = timelineEntry;
        });
    }

    private createRouteTimelineEntry(previousEntry: IOptimisedRouteTimelineEntry, name: string, distance: number, travelTime: number, activityTime: number, shiftStartTime: moment.Moment): IOptimisedRouteTimelineEntry {
        var entry: IOptimisedRouteTimelineEntry = {
            travelDistance: distance,
            activityDuration: activityTime,
            travelTime: travelTime,
            cumulativeDistance: 0,
            cumulativeDuration: 0,
            arrivalTime: shiftStartTime,
            departureTime: shiftStartTime,
            title: name
        };

        if (previousEntry) {
            entry.cumulativeDuration += previousEntry.cumulativeDuration + travelTime;
            entry.cumulativeDistance += previousEntry.cumulativeDistance + distance;

            entry.arrivalTime = shiftStartTime.clone().add(entry.cumulativeDuration, "s");
        }

        entry.cumulativeDuration += + activityTime;
        entry.departureTime = entry.arrivalTime.clone().add(entry.activityDuration, "s");

        return entry
    }
}
